import toast from 'react-hot-toast';

class NotificationService {
  constructor() {
    this.soundEnabled = this.getSetting('soundEnabled', true);
    this.browserNotificationsEnabled = this.getSetting('browserNotificationsEnabled', true);
    this.notificationPermission = null;
    this.audio = null;
    // Deduplikacja powiadomień - śledź ostatnie powiadomienia aby uniknąć duplikatów
    this.recentNotifications = new Map(); // messageId -> timestamp
    this.initAudio();
    this.requestNotificationPermission();
  }

  initAudio() {
    try {
      // Użyj ścieżki względnej do public folder
      this.audio = new Audio(`${window.location.origin}/sounds/message-notification.mp3`);
      this.audio.volume = 0.5;
      this.audio.preload = 'auto';
      
      // Obsługa błędów ładowania - wyłącz dźwięk jeśli plik nie istnieje
      this.audio.addEventListener('error', (e) => {
        console.warn('Could not load notification sound file - disabling sound notifications');
        this.audio = null;
        this.soundEnabled = false;
        this.setSetting('soundEnabled', false);
      });
      
      // Sprawdź czy plik się załadował po krótkim czasie
      setTimeout(() => {
        if (this.audio && this.audio.readyState === 0) {
          // Plik nie załadował się - wyłącz dźwięk
          console.warn('Notification sound file not available - disabling sound notifications');
          this.audio = null;
          this.soundEnabled = false;
          this.setSetting('soundEnabled', false);
        }
      }, 2000);
    } catch (error) {
      console.warn('Could not initialize notification sound:', error);
      this.audio = null;
      this.soundEnabled = false;
      this.setSetting('soundEnabled', false);
    }
  }

  getSetting(key, defaultValue) {
    const stored = localStorage.getItem(`chat_${key}`);
    if (stored === null) {
      return defaultValue;
    }
    return stored === 'true';
  }

  setSetting(key, value) {
    localStorage.setItem(`chat_${key}`, value.toString());
    if (key === 'soundEnabled') {
      this.soundEnabled = value;
    } else if (key === 'browserNotificationsEnabled') {
      this.browserNotificationsEnabled = value;
    }
  }

  async requestNotificationPermission() {
    if ('Notification' in window && this.browserNotificationsEnabled) {
      if (Notification.permission === 'default') {
        // Request permission musi być wywołane z event handlera użytkownika
        // Zapisz callback do wywołania przy pierwszym powiadomieniu
        this.pendingPermissionRequest = true;
      } else {
        this.notificationPermission = Notification.permission;
      }
    }
  }

  async requestPermissionFromUserAction() {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        this.notificationPermission = await Notification.requestPermission();
        this.pendingPermissionRequest = false;
      } catch (error) {
        console.warn('Could not request notification permission:', error);
      }
    }
  }

  playSound() {
    if (this.soundEnabled && this.audio) {
      try {
        this.audio.play().catch(error => {
          console.warn('Could not play notification sound:', error);
        });
      } catch (error) {
        console.warn('Error playing sound:', error);
      }
    }
  }

  showBrowserNotification(senderName, content, onClick) {
    if (!('Notification' in window) || !this.browserNotificationsEnabled) {
      return;
    }

    if (this.notificationPermission === 'granted') {
      const notification = new Notification(`Nowa wiadomość od ${senderName}`, {
        body: content.length > 100 ? content.substring(0, 100) + '...' : content,
        icon: '/images/cropped-logo-szpital_akredytacja.png',
        badge: '/images/cropped-logo-szpital_akredytacja.png',
        tag: 'chat-message',
        requireInteraction: false
      });

      if (onClick) {
        notification.onclick = () => {
          window.focus();
          onClick();
          notification.close();
        };
      }

      // Automatyczne zamknięcie po 5 sekundach
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  }

  showToast(senderName, onClick) {
    toast(
      (t) => (
        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <p className="font-medium text-gray-900">Nowa wiadomość</p>
            <p className="text-sm text-gray-500">od {senderName}</p>
          </div>
          {onClick && (
            <button
              onClick={() => {
                onClick();
                toast.dismiss(t.id);
              }}
              className="px-3 py-1 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Otwórz
            </button>
          )}
        </div>
      ),
      {
        icon: '💬',
        duration: 5000,
        position: 'bottom-right'
      }
    );
  }

  notifyNewMessage(message, options = {}) {
    const {
      isChatMinimized = true,
      isActiveConversation = false,
      currentUserId = null,
      onOpenChat = null
    } = options;

    // Nie pokazuj powiadomień jeśli użytkownik jest w aktywnej rozmowie z nadawcą
    if (isActiveConversation && message.senderId === currentUserId) {
      return;
    }

    // Deduplikacja - sprawdź czy już pokazaliśmy powiadomienie dla tej wiadomości
    const messageId = message.id;
    if (messageId) {
      const lastNotificationTime = this.recentNotifications.get(messageId);
      const now = Date.now();
      
      // Jeśli powiadomienie dla tej wiadomości było pokazane w ciągu ostatnich 5 sekund, zignoruj
      if (lastNotificationTime && (now - lastNotificationTime) < 5000) {
        return;
      }
      
      // Zapisz czas powiadomienia
      this.recentNotifications.set(messageId, now);
      
      // Wyczyść stare wpisy (starsze niż 1 minuta) aby nie zaśmiecać pamięci
      if (this.recentNotifications.size > 100) {
        for (const [id, time] of this.recentNotifications.entries()) {
          if (now - time > 60000) {
            this.recentNotifications.delete(id);
          }
        }
      }
    }

    const senderName = message.senderName || message.senderEmail || 'Nieznany';
    const content = message.content || '';

    // Sprawdź czy aplikacja jest w focusie
    const isAppFocused = document.hasFocus();

    // Dźwięk - zawsze gdy chat zminimalizowany lub inna konwersacja
    if (isChatMinimized || !isActiveConversation) {
      this.playSound();
    }

    // Toast - tylko gdy chat zminimalizowany lub inna konwersacja
    if (isChatMinimized || !isActiveConversation) {
      this.showToast(senderName, onOpenChat);
    }

    // Browser notification - tylko gdy aplikacja nie jest w focusie lub jest w tle
    // Użyj setTimeout aby wywołać w kontekście event loop (nie bezpośrednio z WebSocket callback)
    if (!isAppFocused || !isActiveConversation) {
      setTimeout(() => {
        this.showBrowserNotification(senderName, content, onOpenChat);
      }, 100);
    }
  }

  enableSound() {
    this.setSetting('soundEnabled', true);
  }

  disableSound() {
    this.setSetting('soundEnabled', false);
  }

  enableBrowserNotifications() {
    this.setSetting('browserNotificationsEnabled', true);
    this.requestNotificationPermission();
  }

  disableBrowserNotifications() {
    this.setSetting('browserNotificationsEnabled', false);
  }

  isSoundEnabled() {
    return this.soundEnabled;
  }

  isBrowserNotificationsEnabled() {
    return this.browserNotificationsEnabled;
  }
}

// Singleton instance
const notificationService = new NotificationService();
export default notificationService;
